import ArticleIcon from "@mui/icons-material/Article";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EastIcon from "@mui/icons-material/East";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import {
  Box,
  Chip,
  Container,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { useThemeMode } from "contexts/ThemeContext";
import { motion } from "framer-motion";
import { handleErrorMessage } from "helpers/methods";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { getRelativeFontSize, mediumFont } from "utils/styles";
import fallbackImage from "../../assets/images/thumb-panacea-insights.jpg";
import { selectAuthenticated, selectId } from "../../redux/authSlice";
import type { BlogListItem } from "./BlogsServices";
import { getAllBlogs, likeDislikeBlog } from "./BlogsServices";
import {
  getBlogsUserPreferences,
  setBlogsUserPreferences,
} from "./blogsUserPreferences";
import { formatDescriptionMarkdown } from "./formatDescriptionMarkdown";

const BLOGS_FILTERS_FOR_RETURN_KEY = "blogs_filters_for_return";

export interface BlogsFiltersSnapshot {
  searchQuery: string;
  selectedTopic: string | null;
  selectedTags: string[];
  currentPage: number;
  itemsPerPage: number;
  viewMode: "classic" | "grid" | "list";
}

const saveBlogsFiltersForReturn = (snapshot: BlogsFiltersSnapshot) => {
  try {
    sessionStorage.setItem(
      BLOGS_FILTERS_FOR_RETURN_KEY,
      JSON.stringify(snapshot),
    );
  } catch {
    // ignore
  }
};

const loadAndClearBlogsFiltersForReturn = (): BlogsFiltersSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(BLOGS_FILTERS_FOR_RETURN_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(BLOGS_FILTERS_FOR_RETURN_KEY);
    return JSON.parse(raw) as BlogsFiltersSnapshot;
  } catch {
    return null;
  }
};

const FALLBACK_IMAGE =
  typeof fallbackImage === "string"
    ? fallbackImage
    : ((fallbackImage as { default?: string })?.default ?? "");

// Blog topics (matching OurFeaturesSection)
const blogTopics = [
  { key: "hydroponics", label: "Hydroponics" },
  { key: "technology", label: "Technology" },
  { key: "innovation", label: "Innovation" },
  { key: "wellness", label: "Wellness" },
  { key: "finance", label: "Finance" },
  { key: "philanthropy", label: "Philanthropy" },
  { key: "entrepreneurship", label: "Entrepreneurship" },
];

// Popular tags
const popularTags = [
  "sustainability",
  "innovation",
  "business",
  "health",
  "technology",
  "finance",
  "wellness",
  "startup",
  "environment",
  "community",
];

const Blogs = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { setMode } = useThemeMode();
  const authenticated = useSelector(selectAuthenticated);
  const userId = useSelector(selectId);
  const prefsLoadedRef = useRef(false);

  const [blogs, setBlogs] = useState<BlogListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [goToPageValue, setGoToPageValue] = useState("");
  const [viewMode, setViewMode] = useState<"classic" | "grid" | "list">(
    "classic",
  );
  const [starredIds, setStarredIds] = useState<Set<string>>(new Set());
  const [starLoadingId, setStarLoadingId] = useState<string | null>(null);

  const handleStarToggle = useCallback(
    async (e: React.MouseEvent, blogId: string) => {
      e.stopPropagation();
      if (!authenticated || starLoadingId != null) return;
      const id = String(blogId);
      const isStarred = starredIds.has(id);
      const newStarred = !isStarred;
      setStarLoadingId(id);
      try {
        await likeDislikeBlog(id, newStarred);
        setStarredIds((prev) => {
          const next = new Set(prev);
          if (newStarred) next.add(id);
          else next.delete(id);
          return next;
        });
      } catch (err: any) {
        handleErrorMessage(err?.message ?? "Failed to update star.");
      } finally {
        setStarLoadingId(null);
      }
    },
    [authenticated, starLoadingId, starredIds],
  );

  // Load and apply saved preferences for logged-in users (once per mount/login)
  useEffect(() => {
    if (!authenticated || !userId || prefsLoadedRef.current) return;
    prefsLoadedRef.current = true;
    const prefs = getBlogsUserPreferences(userId);
    if (prefs) {
      if (prefs.viewMode) setViewMode(prefs.viewMode);
      if (prefs.itemsPerPage != null) setItemsPerPage(prefs.itemsPerPage);
      if (prefs.themeMode) setMode(prefs.themeMode);
    }
  }, [authenticated, userId, setMode]);

  // Reset prefs-loaded flag when user logs out so next login can load again
  useEffect(() => {
    if (!authenticated) prefsLoadedRef.current = false;
  }, [authenticated]);

  // Persist viewMode and itemsPerPage when they change (logged-in users only)
  useEffect(() => {
    if (!authenticated || !userId) return;
    setBlogsUserPreferences(userId, { viewMode, itemsPerPage });
  }, [authenticated, userId, viewMode, itemsPerPage]);

  // Restore filters when returning from a blog detail page (saved on navigate to blog)
  useEffect(() => {
    const snapshot = loadAndClearBlogsFiltersForReturn();
    if (!snapshot) return;
    setSearchQuery(snapshot.searchQuery ?? "");
    setSelectedTopic(snapshot.selectedTopic ?? null);
    setSelectedTags(
      Array.isArray(snapshot.selectedTags) ? snapshot.selectedTags : [],
    );
    setCurrentPage(
      typeof snapshot.currentPage === "number" && snapshot.currentPage >= 1
        ? snapshot.currentPage
        : 1,
    );
    setItemsPerPage(
      typeof snapshot.itemsPerPage === "number" && snapshot.itemsPerPage >= 1
        ? snapshot.itemsPerPage
        : 5,
    );
    if (
      snapshot.viewMode === "classic" ||
      snapshot.viewMode === "grid" ||
      snapshot.viewMode === "list"
    ) {
      setViewMode(snapshot.viewMode);
    }
  }, []);

  // Apply topic filter from URL query (?topic=...) when navigating from Home features
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const topicFromUrl = params.get("topic");
    if (topicFromUrl && blogTopics.some((t) => t.key === topicFromUrl)) {
      setSelectedTopic(topicFromUrl);
    }
  }, [location.search]);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getAllBlogs();
      setBlogs(Array.isArray(data) ? data : []);
      const starredIds = data
        .filter((blog) => blog.likes > 0)
        .map((blog) => String(blog.id));
      setStarredIds(new Set(starredIds.map((id) => String(id))));
    } catch (err: any) {
      setError(err?.message ?? "Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
  }, [fetchBlogs]);

  // Filter blogs
  const filteredBlogs = blogs.filter((blog) => {
    const title = typeof blog.title === "string" ? blog.title : "";
    const description =
      typeof blog.description === "string" ? blog.description : "";
    const author = typeof blog.author === "string" ? blog.author : "";
    const q = (searchQuery || "").toLowerCase();
    const matchesSearch =
      !q ||
      title.toLowerCase().includes(q) ||
      description.toLowerCase().includes(q) ||
      author.toLowerCase().includes(q);

    const matchesTopic =
      !selectedTopic ||
      (typeof blog.topic === "string" &&
        typeof selectedTopic === "string" &&
        blog.topic.toLowerCase() === selectedTopic.toLowerCase());

    const matchesTags =
      selectedTags.length === 0 ||
      selectedTags.some((tag) => blog.tags.includes(tag));

    return matchesSearch && matchesTopic && matchesTags;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredBlogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBlogs = filteredBlogs.slice(startIndex, endIndex);

  // Reset to page 1 when filters or items per page change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedTopic, selectedTags, itemsPerPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    setItemsPerPage(Number(event.target.value));
  };

  const handleGoToPage = () => {
    const page = parseInt(goToPageValue);
    if (page >= 1 && page <= totalPages) {
      handlePageChange(page);
      setGoToPageValue("");
    }
  };

  const handleRandomPage = () => {
    const randomPage = Math.floor(Math.random() * totalPages) + 1;
    handlePageChange(randomPage);
  };

  const navigateToBlog = (blogId: string) => {
    saveBlogsFiltersForReturn({
      searchQuery,
      selectedTopic,
      selectedTags,
      currentPage,
      itemsPerPage,
      viewMode,
    });
    history.push(`/blog/${blogId}`);
  };

  // Get top blogs for recommendations (first 3)
  const topBlogs = blogs.slice(0, 3);

  // Count blogs per topic
  const getTopicCount = (topicKey: string) => {
    return blogs.filter(
      (blog) =>
        (typeof blog.topic === "string" ? blog.topic.toLowerCase() : "") ===
        topicKey.toLowerCase(),
    ).length;
  };

  const handleTagClick = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTopicLabel = (topicKey: string) => {
    return blogTopics.find((t) => t.key === topicKey)?.label || topicKey;
  };

  return (
    <>
      <style>
        {`
          @keyframes gradientShift {
            0%, 100% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 145% 50%;
            }
          }
          @keyframes textReveal {
            from {
              opacity: 0;
              transform: translateY(30px) scale(0.95);
              clip-path: inset(0 100% 0 0);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
              clip-path: inset(0 0% 0 0);
            }
          }
          @keyframes textGlowIn {
            0% {
              opacity: 0;
              transform: scale(0.8);
              text-shadow: 0 0 0px rgba(248, 72, 28, 0);
            }
            50% {
              opacity: 0.8;
              transform: scale(1.05);
              text-shadow: 0 0 20px rgba(248, 72, 28, 0.5);
            }
            100% {
              opacity: 1;
              transform: scale(1);
              text-shadow: 0 0 0px rgba(248, 72, 28, 0);
            }
          }
          @keyframes accentLineDraw {
            from {
              width: 0;
              opacity: 0;
            }
            to {
              width: 100%;
              opacity: 1;
            }
          }
          @keyframes blogPostReveal {
            0% { opacity: 0; transform: translateY(30px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes blogImageHover {
            0% { transform: scale(1); }
            100% { transform: scale(1.05); }
          }
          .blog-description-content {
            margin: 0;
          }
          .blog-description-content p,
          .blog-description-content div {
            margin: 0 0 0.5em;
            display: inline;
          }
          .blog-description-content p:last-child,
          .blog-description-content div:last-child {
            margin-bottom: 0;
          }
          .blog-description-content strong,
          .blog-description-content b {
            font-weight: 700;
          }
          .blog-description-content em,
          .blog-description-content i {
            font-style: italic;
          }
          .blog-description-content a {
            color: inherit;
            text-decoration: underline;
          }
          .blog-description-content ul,
          .blog-description-content ol {
            margin: 0 0 0.5em;
            padding-left: 1.25em;
          }
          .blog-description-content br {
            display: block;
            content: "";
            margin-top: 0.25em;
          }
          .blog-description-content .blog-desc-hr {
            border: none;
            border-top: 1px solid currentColor;
            opacity: 0.25;
            margin: 0.75em 0;
          }
          .blog-description-content .blog-desc-quote {
            margin: 0.5em 0;
            padding-left: 1em;
            border-left: 3px solid currentColor;
            opacity: 0.9;
          }
          .blog-description-content .blog-desc-h1 {
            font-weight: 700;
            display: block;
            margin-bottom: 0.25em;
          }
          .blog-description-content .blog-desc-h2 {
            font-weight: 700;
            font-size: 0.95em;
            display: block;
            margin-bottom: 0.2em;
          }
          .blog-description-content .blog-desc-h3 {
            font-weight: 600;
            font-size: 0.9em;
            display: block;
            margin-bottom: 0.2em;
          }
        `}
      </style>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: "100vh",
          backgroundColor: theme.palette.background.default,
          overflow: "hidden",
          pb: { xs: 6, md: 8 },
        }}
      >
        {/* Static Creative Background */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            pointerEvents: "none",
            zIndex: 0,
          }}
        >
          {/* Large gradient orbs - neutral colors, more visible */}
          <Box
            sx={{
              position: "absolute",
              top: "-10%",
              right: "-8%",
              width: "600px",
              height: "600px",
              borderRadius: "50%",
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(circle, ${alpha(
                      "#ffffff",
                      0.08,
                    )} 0%, ${alpha("#ffffff", 0.04)} 30%, transparent 60%)`
                  : `radial-gradient(circle, ${alpha(
                      "#000000",
                      0.06,
                    )} 0%, ${alpha("#000000", 0.03)} 30%, transparent 60%)`,
              filter: "blur(50px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-8%",
              left: "-6%",
              width: "550px",
              height: "550px",
              borderRadius: "50%",
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(circle, ${alpha(
                      "#ffffff",
                      0.06,
                    )} 0%, ${alpha("#ffffff", 0.03)} 30%, transparent 60%)`
                  : `radial-gradient(circle, ${alpha(
                      "#000000",
                      0.05,
                    )} 0%, ${alpha("#000000", 0.02)} 30%, transparent 60%)`,
              filter: "blur(50px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "45%",
              left: "52%",
              transform: "translate(-50%, -50%)",
              width: "400px",
              height: "400px",
              borderRadius: "50%",
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(circle, ${alpha(
                      "#ffffff",
                      0.05,
                    )} 0%, ${alpha("#ffffff", 0.02)} 30%, transparent 60%)`
                  : `radial-gradient(circle, ${alpha(
                      "#000000",
                      0.04,
                    )} 0%, ${alpha("#000000", 0.02)} 30%, transparent 60%)`,
              filter: "blur(60px)",
            }}
          />

          {/* Additional smaller accent orbs */}
          <Box
            sx={{
              position: "absolute",
              top: "20%",
              left: "10%",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(circle, ${alpha(
                      "#ffffff",
                      0.06,
                    )} 0%, transparent 50%)`
                  : `radial-gradient(circle, ${alpha(
                      "#000000",
                      0.05,
                    )} 0%, transparent 50%)`,
              filter: "blur(30px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "25%",
              right: "15%",
              width: "180px",
              height: "180px",
              borderRadius: "50%",
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(circle, ${alpha(
                      "#ffffff",
                      0.05,
                    )} 0%, transparent 50%)`
                  : `radial-gradient(circle, ${alpha(
                      "#000000",
                      0.04,
                    )} 0%, transparent 50%)`,
              filter: "blur(30px)",
            }}
          />

          {/* Gradient mesh overlay - neutral colors */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                theme.palette.mode === "dark"
                  ? `radial-gradient(ellipse at 25% 20%, ${alpha(
                      "#ffffff",
                      0.05,
                    )} 0%, transparent 50%),
                   radial-gradient(ellipse at 75% 80%, ${alpha(
                     "#ffffff",
                     0.04,
                   )} 0%, transparent 50%),
                   radial-gradient(ellipse at 50% 50%, ${alpha(
                     "#ffffff",
                     0.03,
                   )} 0%, transparent 50%)`
                  : `radial-gradient(ellipse at 25% 20%, ${alpha(
                      "#000000",
                      0.04,
                    )} 0%, transparent 50%),
                   radial-gradient(ellipse at 75% 80%, ${alpha(
                     "#000000",
                     0.03,
                   )} 0%, transparent 50%),
                   radial-gradient(ellipse at 50% 50%, ${alpha(
                     "#000000",
                     0.02,
                   )} 0%, transparent 50%)`,
            }}
          />

          {/* Geometric shapes - neutral colors */}
          <Box
            sx={{
              position: "absolute",
              top: "15%",
              right: "20%",
              width: "120px",
              height: "120px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(135deg, ${alpha(
                      "#ffffff",
                      0.08,
                    )} 0%, transparent 100%)`
                  : `linear-gradient(135deg, ${alpha(
                      "#000000",
                      0.06,
                    )} 0%, transparent 100%)`,
              transform: "rotate(45deg)",
              borderRadius: "8px",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "20%",
              left: "25%",
              width: "100px",
              height: "100px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(45deg, ${alpha(
                      "#ffffff",
                      0.06,
                    )} 0%, transparent 100%)`
                  : `linear-gradient(45deg, ${alpha(
                      "#000000",
                      0.05,
                    )} 0%, transparent 100%)`,
              transform: "rotate(-45deg)",
              borderRadius: "8px",
            }}
          />

          {/* Decorative lines - neutral colors */}
          <Box
            sx={{
              position: "absolute",
              top: "30%",
              left: "5%",
              width: "2px",
              height: "200px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(180deg, transparent 0%, ${alpha(
                      "#ffffff",
                      0.12,
                    )} 50%, transparent 100%)`
                  : `linear-gradient(180deg, transparent 0%, ${alpha(
                      "#000000",
                      0.1,
                    )} 50%, transparent 100%)`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "30%",
              right: "8%",
              width: "2px",
              height: "180px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(180deg, transparent 0%, ${alpha(
                      "#ffffff",
                      0.1,
                    )} 50%, transparent 100%)`
                  : `linear-gradient(180deg, transparent 0%, ${alpha(
                      "#000000",
                      0.08,
                    )} 50%, transparent 100%)`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "60%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "300px",
              height: "2px",
              background:
                theme.palette.mode === "dark"
                  ? `linear-gradient(90deg, transparent 0%, ${alpha(
                      "#ffffff",
                      0.08,
                    )} 50%, transparent 100%)`
                  : `linear-gradient(90deg, transparent 0%, ${alpha(
                      "#000000",
                      0.07,
                    )} 50%, transparent 100%)`,
            }}
          />

          {/* Enhanced grid pattern - more visible */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `linear-gradient(${alpha(
                theme.palette.divider,
                0.15,
              )} 1px, transparent 1px), 
                               linear-gradient(90deg, ${alpha(
                                 theme.palette.divider,
                                 0.15,
                               )} 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
              opacity: 0.6,
            }}
          />

          {/* Subtle diagonal lines - neutral colors */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage:
                theme.palette.mode === "dark"
                  ? `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 2px,
                    ${alpha("#ffffff", 0.05)} 2px,
                    ${alpha("#ffffff", 0.05)} 4px
                  )`
                  : `repeating-linear-gradient(
                    45deg,
                    transparent,
                    transparent 2px,
                    ${alpha("#000000", 0.04)} 2px,
                    ${alpha("#000000", 0.04)} 4px
                  )`,
              opacity: 0.6,
            }}
          />

          {/* Circular rings - clearly visible */}
          <Box
            sx={{
              position: "absolute",
              top: "25%",
              right: "12%",
              width: "150px",
              height: "150px",
              border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
              borderRadius: "50%",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "35%",
              left: "15%",
              width: "120px",
              height: "120px",
              border: `2px solid ${alpha(theme.palette.divider, 0.25)}`,
              borderRadius: "50%",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "55%",
              right: "25%",
              width: "100px",
              height: "100px",
              border: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: "50%",
            }}
          />

          {/* Dots/particles - clearly visible */}
          {[
            { top: "18%", left: "8%", size: 6 },
            { top: "22%", left: "12%", size: 4 },
            { top: "28%", left: "6%", size: 5 },
            { bottom: "22%", right: "10%", size: 6 },
            { bottom: "28%", right: "14%", size: 4 },
            { bottom: "32%", right: "8%", size: 5 },
            { top: "50%", right: "18%", size: 5 },
            { top: "65%", left: "20%", size: 6 },
            { top: "70%", left: "24%", size: 4 },
            { bottom: "45%", left: "30%", size: 5 },
          ].map((dot, i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                ...(dot.top && { top: dot.top }),
                ...(dot.bottom && { bottom: dot.bottom }),
                ...(dot.left && { left: dot.left }),
                ...(dot.right && { right: dot.right }),
                width: `${dot.size}px`,
                height: `${dot.size}px`,
                borderRadius: "50%",
                background: alpha(theme.palette.divider, 0.4),
                border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            />
          ))}

          {/* Square shapes with borders - clearly visible */}
          <Box
            sx={{
              position: "absolute",
              top: "12%",
              left: "18%",
              width: "80px",
              height: "80px",
              border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
              transform: "rotate(15deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "18%",
              right: "22%",
              width: "70px",
              height: "70px",
              border: `2px solid ${alpha(theme.palette.divider, 0.25)}`,
              transform: "rotate(-20deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "75%",
              left: "12%",
              width: "60px",
              height: "60px",
              border: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
              transform: "rotate(30deg)",
            }}
          />

          {/* Connected line paths */}
          <Box
            sx={{
              position: "absolute",
              top: "40%",
              left: "8%",
              width: "150px",
              height: "2px",
              background: `linear-gradient(90deg, ${alpha(
                theme.palette.divider,
                0.3,
              )} 0%, transparent 100%)`,
              transform: "rotate(25deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "40%",
              right: "12%",
              width: "120px",
              height: "2px",
              background: `linear-gradient(90deg, transparent 0%, ${alpha(
                theme.palette.divider,
                0.3,
              )} 100%)`,
              transform: "rotate(-25deg)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "35%",
              right: "30%",
              width: "2px",
              height: "100px",
              background: `linear-gradient(180deg, ${alpha(
                theme.palette.divider,
                0.25,
              )} 0%, transparent 100%)`,
            }}
          />

          {/* Hexagon shape */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "35%",
              width: "60px",
              height: "60px",
              border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
              clipPath:
                "polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)",
            }}
          />

          {/* Large decorative circle outline */}
          <Box
            sx={{
              position: "absolute",
              top: "10%",
              right: "30%",
              width: "200px",
              height: "200px",
              border: `3px solid ${alpha(theme.palette.divider, 0.2)}`,
              borderRadius: "50%",
              opacity: 0.7,
            }}
          />
        </Box>

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
          {/* Page Header */}
          <Box
            sx={{
              pt: { xs: 12, md: 14 },
              position: "relative",
            }}
          >
            {/* Header Background Effects */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "-10%",
                right: "-10%",
                bottom: 0,
                background:
                  theme.palette.mode === "dark"
                    ? `radial-gradient(ellipse at center top, ${alpha(
                        "#f8481c",
                        0.08,
                      )} 0%, transparent 60%)`
                    : `radial-gradient(ellipse at center top, ${alpha(
                        "#f8481c",
                        0.05,
                      )} 0%, transparent 60%)`,
                pointerEvents: "none",
              }}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 3,
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                style={{ flex: 1 }}
              >
                <Typography
                  sx={{
                    ...mediumFont,
                    fontSize: getRelativeFontSize(30),
                    background:
                      theme.palette.mode === "dark"
                        ? "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 65%, #f8481c 85%, #ff6b4a 100%)"
                        : "linear-gradient(135deg, #000000 0%, #000000 65%, #f8481c 85%, #ff6b4a 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundSize: "300% 300%",
                    animation:
                      "gradientShift 5s ease infinite, textReveal 1.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both, textGlowIn 1.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.1s both",
                    letterSpacing: "-0.04em",
                    lineHeight: 1.3,
                    mb: 1,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: "-12px",
                      left: 0,
                      width: "0%",
                      height: "4px",
                      background:
                        "linear-gradient(90deg, #f8481c 0%, #ff6b4a 100%)",
                      animation:
                        "accentLineDraw 1.2s cubic-bezier(0.34, 1.56, 0.64, 1) 1.2s both",
                    },
                  }}
                >
                  {t("Blogs")}
                </Typography>
              </motion.div>

              {/* View Toggle */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <Stack direction="row" spacing={0.5}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Box
                      onClick={() => setViewMode("classic")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        height: "40px",
                        cursor: "pointer",
                        background:
                          viewMode === "classic"
                            ? `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.2,
                              )} 0%, ${alpha("#f8481c", 0.15)} 100%)`
                            : theme.palette.mode === "dark"
                              ? alpha("#1b1b1b", 0.8)
                              : alpha("#ffffff", 0.8),
                        border: `1px solid ${
                          viewMode === "classic"
                            ? alpha("#f8481c", 0.5)
                            : alpha(theme.palette.divider, 0.2)
                        }`,
                        borderLeft:
                          viewMode === "classic"
                            ? `3px solid ${alpha("#f8481c", 0.8)}`
                            : undefined,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background:
                            viewMode === "classic"
                              ? undefined
                              : alpha("#f8481c", 0.1),
                          border:
                            viewMode === "classic"
                              ? undefined
                              : `1px solid ${alpha("#f8481c", 0.3)}`,
                        },
                      }}
                    >
                      <ViewComfyIcon
                        sx={{
                          fontSize: "20px",
                          color:
                            viewMode === "classic"
                              ? "#f8481c"
                              : theme.palette.text.primary,
                        }}
                      />
                    </Box>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Box
                      onClick={() => setViewMode("grid")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        height: "40px",
                        cursor: "pointer",
                        background:
                          viewMode === "grid"
                            ? `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.2,
                              )} 0%, ${alpha("#f8481c", 0.15)} 100%)`
                            : theme.palette.mode === "dark"
                              ? alpha("#1b1b1b", 0.8)
                              : alpha("#ffffff", 0.8),
                        border: `1px solid ${
                          viewMode === "grid"
                            ? alpha("#f8481c", 0.5)
                            : alpha(theme.palette.divider, 0.2)
                        }`,
                        borderLeft:
                          viewMode === "grid"
                            ? `3px solid ${alpha("#f8481c", 0.8)}`
                            : undefined,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background:
                            viewMode === "grid"
                              ? undefined
                              : alpha("#f8481c", 0.1),
                          border:
                            viewMode === "grid"
                              ? undefined
                              : `1px solid ${alpha("#f8481c", 0.3)}`,
                        },
                      }}
                    >
                      <ViewModuleIcon
                        sx={{
                          fontSize: "20px",
                          color:
                            viewMode === "grid"
                              ? "#f8481c"
                              : theme.palette.text.primary,
                        }}
                      />
                    </Box>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Box
                      onClick={() => setViewMode("list")}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "40px",
                        height: "40px",
                        cursor: "pointer",
                        background:
                          viewMode === "list"
                            ? `linear-gradient(135deg, ${alpha(
                                "#f8481c",
                                0.2,
                              )} 0%, ${alpha("#f8481c", 0.15)} 100%)`
                            : theme.palette.mode === "dark"
                              ? alpha("#1b1b1b", 0.8)
                              : alpha("#ffffff", 0.8),
                        border: `1px solid ${
                          viewMode === "list"
                            ? alpha("#f8481c", 0.5)
                            : alpha(theme.palette.divider, 0.2)
                        }`,
                        borderLeft:
                          viewMode === "list"
                            ? `3px solid ${alpha("#f8481c", 0.8)}`
                            : undefined,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background:
                            viewMode === "list"
                              ? undefined
                              : alpha("#f8481c", 0.1),
                          border:
                            viewMode === "list"
                              ? undefined
                              : `1px solid ${alpha("#f8481c", 0.3)}`,
                        },
                      }}
                    >
                      <ViewListIcon
                        sx={{
                          fontSize: "20px",
                          color:
                            viewMode === "list"
                              ? "#f8481c"
                              : theme.palette.text.primary,
                        }}
                      />
                    </Box>
                  </motion.div>
                </Stack>
              </motion.div>
            </Box>
          </Box>

          <Grid container spacing={12}>
            {/* Main Content - Blog Posts */}
            <Grid item xs={12} lg={8}>
              {loading && (
                <Stack spacing={3} sx={{ py: 6 }}>
                  {[1, 2, 3].map((i) => (
                    <Box
                      key={i}
                      sx={{
                        height: { xs: "280px", md: "400px" },
                        borderRadius: 2,
                        background: alpha(theme.palette.divider, 0.2),
                      }}
                    />
                  ))}
                </Stack>
              )}
              {!loading && error && (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 8,
                    px: 3,
                    background: alpha(theme.palette.background.paper, 0.8),
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    sx={{ color: theme.palette.text.secondary, mb: 2 }}
                  >
                    {error}
                  </Typography>
                  <Typography
                    component="button"
                    onClick={() => fetchBlogs()}
                    sx={{
                      cursor: "pointer",
                      color: "#f8481c",
                      fontWeight: 600,
                      textDecoration: "underline",
                      border: "none",
                      background: "none",
                    }}
                  >
                    Retry
                  </Typography>
                </Box>
              )}
              {!loading && !error && (
                <>
                  {/* Blog Posts - Conditional Rendering Based on View Mode */}
                  {viewMode === "classic" && (
                    <Stack spacing={5}>
                      {paginatedBlogs.map((blog, index) => (
                        <motion.div
                          key={blog.id}
                          initial={{ opacity: 0, y: 40 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                        >
                          <Box
                            onClick={() => navigateToBlog(String(blog.id))}
                            sx={{
                              position: "relative",
                              overflow: "hidden",
                              background:
                                theme.palette.mode === "dark"
                                  ? `linear-gradient(135deg, ${alpha(
                                      "#1b1b1b",
                                      0.95,
                                    )} 0%, ${alpha("#252525", 0.95)} 100%)`
                                  : `linear-gradient(135deg, ${alpha(
                                      "#ffffff",
                                      0.95,
                                    )} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.1,
                              )}`,
                              borderLeft: `3px solid ${alpha("#f8481c", 0.3)}`,
                              boxShadow: `0 2px 12px ${alpha(
                                theme.palette.mode === "dark"
                                  ? "#000000"
                                  : "#000000",
                                0.08,
                              )}`,
                              transition:
                                "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                              cursor: "pointer",
                              "&:hover": {
                                boxShadow: `0 4px 24px ${alpha(
                                  theme.palette.mode === "dark"
                                    ? "#000000"
                                    : "#000000",
                                  0.12,
                                )}`,
                                borderLeft: `3px solid ${alpha(
                                  "#f8481c",
                                  0.8,
                                )}`,
                                transform: "translateY(-2px)",
                                "& .blog-post-image": {
                                  transform: "scale(1.05)",
                                },
                                "& .read-more-arrow": {
                                  transform: "translateX(6px)",
                                },
                              },
                            }}
                          >
                            {/* Featured Image */}
                            <Box
                              sx={{
                                position: "relative",
                                width: "100%",
                                height: { xs: "280px", md: "400px" },
                                overflow: "hidden",
                                background: alpha("#f8481c", 0.05),
                              }}
                            >
                              <Box
                                component="img"
                                src={blog.image}
                                alt={blog.title}
                                className="blog-post-image"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  if (target && FALLBACK_IMAGE)
                                    target.src = FALLBACK_IMAGE;
                                }}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  transition:
                                    "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: `linear-gradient(180deg, transparent 0%, ${alpha(
                                    "#f8481c",
                                    0.2,
                                  )} 100%)`,
                                  opacity: 0.6,
                                }}
                              />
                              {/* Topic Badge */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 20,
                                  left: 20,
                                  px: 2,
                                  py: 1,
                                  background: alpha("#f8481c", 0.95),
                                  backdropFilter: "blur(10px)",
                                  boxShadow: `0 2px 12px ${alpha(
                                    "#f8481c",
                                    0.4,
                                  )}`,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 700,
                                    color: "#ffffff",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  {getTopicLabel(blog.topic)}
                                </Typography>
                              </Box>
                              {authenticated && (
                                <IconButton
                                  size="small"
                                  disabled={starLoadingId === String(blog.id)}
                                  onClick={(e) =>
                                    handleStarToggle(e, String(blog.id))
                                  }
                                  aria-label={
                                    starredIds.has(String(blog.id))
                                      ? "Unstar blog"
                                      : "Star blog"
                                  }
                                  sx={{
                                    position: "absolute",
                                    top: 20,
                                    right: 20,
                                    bgcolor: alpha(
                                      theme.palette.background.paper,
                                      0.9,
                                    ),
                                    color: starredIds.has(String(blog.id))
                                      ? "#f8481c"
                                      : theme.palette.text.secondary,
                                    width: 40,
                                    height: 40,
                                    "&:hover": {
                                      bgcolor: alpha("#f8481c", 0.15),
                                      color: "#f8481c",
                                    },
                                  }}
                                >
                                  {starredIds.has(String(blog.id)) ? (
                                    <StarIcon sx={{ fontSize: 22 }} />
                                  ) : (
                                    <StarBorderIcon sx={{ fontSize: 22 }} />
                                  )}
                                </IconButton>
                              )}
                            </Box>

                            {/* Content */}
                            <Stack spacing={2.5} p={{ xs: 3, md: 4 }}>
                              {/* Title */}
                              <Typography
                                component="h2"
                                sx={{
                                  fontSize: { xs: "24px", md: "32px" },
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  lineHeight: 1.3,
                                  transition: "color 0.3s ease",
                                  "&:hover": {
                                    color: "#f8481c",
                                  },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatDescriptionMarkdown(
                                    typeof blog.title === "string"
                                      ? blog.title
                                      : "",
                                  ),
                                }}
                              />

                              {/* Meta Info */}
                              <Stack
                                direction="row"
                                spacing={3}
                                flexWrap="wrap"
                                sx={{
                                  pb: 1,
                                  borderBottom: `1px solid ${alpha(
                                    theme.palette.divider,
                                    0.1,
                                  )}`,
                                }}
                              >
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <CalendarTodayIcon
                                    sx={{
                                      fontSize: "16px",
                                      color: "#f8481c",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: theme.palette.text.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatDate(blog.publishDate)}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <PersonIcon
                                    sx={{
                                      fontSize: "16px",
                                      color: "#f8481c",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "14px",
                                      color: theme.palette.text.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {blog.author}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  useFlexGap
                                >
                                  {blog.tags.slice(0, 2).map((tag: string) => (
                                    <Chip
                                      key={tag}
                                      label={tag}
                                      size="small"
                                      sx={{
                                        fontSize: "11px",
                                        height: "24px",
                                        background: alpha("#f8481c", 0.12),
                                        color: "#f8481c",
                                        border: `1px solid ${alpha(
                                          "#f8481c",
                                          0.25,
                                        )}`,
                                        borderRadius: 0,
                                        fontWeight: 600,
                                      }}
                                    />
                                  ))}
                                </Stack>
                              </Stack>

                              {/* Description - formatted markup (e.g. OpenAI HTML) */}
                              <Box
                                className="blog-description-content"
                                sx={{
                                  fontSize: { xs: "15px", md: "16px" },
                                  color: theme.palette.text.secondary,
                                  lineHeight: 1.7,
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatDescriptionMarkdown(
                                    typeof blog.description === "string"
                                      ? blog.description
                                      : "",
                                  ),
                                }}
                              />

                              {/* Read More Link */}
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                  pt: 1,
                                  cursor: "pointer",
                                  width: "fit-content",
                                  "&:hover": {
                                    "& .read-more-text": {
                                      color: "#f8481c",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  className="read-more-text"
                                  sx={{
                                    fontSize: "15px",
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    transition: "color 0.3s ease",
                                  }}
                                >
                                  READ MORE
                                </Typography>
                                <motion.div
                                  className="read-more-arrow"
                                  animate={{ x: [0, 4, 0] }}
                                  transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                  }}
                                >
                                  <EastIcon
                                    sx={{
                                      fontSize: "18px",
                                      color: "#f8481c",
                                    }}
                                  />
                                </motion.div>
                              </Stack>
                            </Stack>
                          </Box>
                        </motion.div>
                      ))}

                      {filteredBlogs.length === 0 && (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 10,
                            px: 3,
                            background:
                              theme.palette.mode === "dark"
                                ? alpha("#1b1b1b", 0.6)
                                : alpha("#ffffff", 0.6),
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.1,
                            )}`,
                            borderLeft: `3px solid ${alpha("#f8481c", 0.3)}`,
                          }}
                        >
                          <ArticleIcon
                            sx={{
                              fontSize: "72px",
                              color: alpha("#f8481c", 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "20px",
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                            }}
                          >
                            No blogs found matching your criteria.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  )}

                  {/* Grid View */}
                  {viewMode === "grid" && (
                    <Grid container spacing={3}>
                      {paginatedBlogs.map((blog, index) => (
                        <Grid item xs={12} sm={6} key={blog.id}>
                          <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.9 }}
                            whileInView={{ opacity: 1, y: 0, scale: 1 }}
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            whileHover={{ y: -4 }}
                          >
                            <Box
                              onClick={() => navigateToBlog(String(blog.id))}
                              sx={{
                                position: "relative",
                                height: "100%",
                                overflow: "hidden",
                                background:
                                  theme.palette.mode === "dark"
                                    ? `linear-gradient(135deg, ${alpha(
                                        "#1b1b1b",
                                        0.95,
                                      )} 0%, ${alpha("#252525", 0.95)} 100%)`
                                    : `linear-gradient(135deg, ${alpha(
                                        "#ffffff",
                                        0.95,
                                      )} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.1,
                                )}`,
                                borderLeft: `3px solid ${alpha(
                                  "#f8481c",
                                  0.3,
                                )}`,
                                boxShadow: `0 2px 12px ${alpha(
                                  theme.palette.mode === "dark"
                                    ? "#000000"
                                    : "#000000",
                                  0.08,
                                )}`,
                                transition:
                                  "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                                cursor: "pointer",
                                "&:hover": {
                                  boxShadow: `0 4px 24px ${alpha(
                                    theme.palette.mode === "dark"
                                      ? "#000000"
                                      : "#000000",
                                    0.12,
                                  )}`,
                                  borderLeft: `3px solid ${alpha(
                                    "#f8481c",
                                    0.8,
                                  )}`,
                                  transform: "translateY(-4px)",
                                  "& .blog-post-image": {
                                    transform: "scale(1.08)",
                                  },
                                },
                              }}
                            >
                              {/* Image */}
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "100%",
                                  height: "220px",
                                  overflow: "hidden",
                                  background: alpha("#f8481c", 0.05),
                                }}
                              >
                                <Box
                                  component="img"
                                  src={blog.image}
                                  alt={blog.title}
                                  className="blog-post-image"
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (target && FALLBACK_IMAGE)
                                      target.src = FALLBACK_IMAGE;
                                  }}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    transition:
                                      "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    background: `linear-gradient(180deg, transparent 0%, ${alpha(
                                      "#f8481c",
                                      0.2,
                                    )} 100%)`,
                                    opacity: 0.6,
                                  }}
                                />
                                {/* Topic Badge */}
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 16,
                                    left: 16,
                                    px: 2,
                                    py: 1,
                                    background: alpha("#f8481c", 0.95),
                                    backdropFilter: "blur(10px)",
                                    boxShadow: `0 2px 12px ${alpha(
                                      "#f8481c",
                                      0.4,
                                    )}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#ffffff",
                                      letterSpacing: "0.5px",
                                    }}
                                  >
                                    {getTopicLabel(blog.topic)}
                                  </Typography>
                                </Box>
                                {authenticated && (
                                  <IconButton
                                    size="small"
                                    disabled={starLoadingId === String(blog.id)}
                                    onClick={(e) =>
                                      handleStarToggle(e, String(blog.id))
                                    }
                                    aria-label={
                                      starredIds.has(String(blog.id))
                                        ? "Unstar blog"
                                        : "Star blog"
                                    }
                                    sx={{
                                      position: "absolute",
                                      top: 16,
                                      right: 16,
                                      bgcolor: alpha(
                                        theme.palette.background.paper,
                                        0.9,
                                      ),
                                      color: starredIds.has(String(blog.id))
                                        ? "#f8481c"
                                        : theme.palette.text.secondary,
                                      width: 36,
                                      height: 36,
                                      "&:hover": {
                                        bgcolor: alpha("#f8481c", 0.15),
                                        color: "#f8481c",
                                      },
                                    }}
                                  >
                                    {starredIds.has(String(blog.id)) ? (
                                      <StarIcon sx={{ fontSize: 20 }} />
                                    ) : (
                                      <StarBorderIcon sx={{ fontSize: 20 }} />
                                    )}
                                  </IconButton>
                                )}
                              </Box>

                              {/* Content */}
                              <Stack spacing={2} p={3}>
                                <Typography
                                  component="h2"
                                  sx={{
                                    fontSize: { xs: "20px", md: "24px" },
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.3,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    transition: "color 0.3s ease",
                                    "&:hover": {
                                      color: "#f8481c",
                                    },
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatDescriptionMarkdown(
                                      typeof blog.title === "string"
                                        ? blog.title
                                        : "",
                                    ),
                                  }}
                                />

                                <Stack
                                  direction="row"
                                  spacing={2}
                                  flexWrap="wrap"
                                >
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <CalendarTodayIcon
                                      sx={{
                                        fontSize: "14px",
                                        color: "#f8481c",
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {formatDate(blog.publishDate)}
                                    </Typography>
                                  </Stack>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <PersonIcon
                                      sx={{
                                        fontSize: "14px",
                                        color: "#f8481c",
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        color: theme.palette.text.secondary,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {blog.author}
                                    </Typography>
                                  </Stack>
                                </Stack>

                                <Box
                                  className="blog-description-content"
                                  sx={{
                                    fontSize: "14px",
                                    color: theme.palette.text.secondary,
                                    lineHeight: 1.6,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatDescriptionMarkdown(
                                      typeof blog.description === "string"
                                        ? blog.description
                                        : "",
                                    ),
                                  }}
                                />

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{
                                    pt: 1,
                                    cursor: "pointer",
                                    width: "fit-content",
                                    "&:hover": {
                                      "& .read-more-text": {
                                        color: "#f8481c",
                                      },
                                    },
                                  }}
                                >
                                  <Typography
                                    className="read-more-text"
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: 600,
                                      color: theme.palette.text.primary,
                                      transition: "color 0.3s ease",
                                    }}
                                  >
                                    READ MORE
                                  </Typography>
                                  <motion.div
                                    className="read-more-arrow"
                                    animate={{ x: [0, 4, 0] }}
                                    transition={{
                                      duration: 1.5,
                                      repeat: Infinity,
                                    }}
                                  >
                                    <EastIcon
                                      sx={{
                                        fontSize: "16px",
                                        color: "#f8481c",
                                      }}
                                    />
                                  </motion.div>
                                </Stack>
                              </Stack>
                            </Box>
                          </motion.div>
                        </Grid>
                      ))}
                      {filteredBlogs.length === 0 && (
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              textAlign: "center",
                              py: 10,
                              px: 3,
                              background:
                                theme.palette.mode === "dark"
                                  ? alpha("#1b1b1b", 0.6)
                                  : alpha("#ffffff", 0.6),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.1,
                              )}`,
                              borderLeft: `3px solid ${alpha("#f8481c", 0.3)}`,
                            }}
                          >
                            <ArticleIcon
                              sx={{
                                fontSize: "72px",
                                color: alpha("#f8481c", 0.3),
                                mb: 2,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "20px",
                                color: theme.palette.text.secondary,
                                fontWeight: 500,
                              }}
                            >
                              No blogs found matching your criteria.
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  )}

                  {/* List View */}
                  {viewMode === "list" && (
                    <Stack spacing={2}>
                      {paginatedBlogs.map((blog, index) => (
                        <motion.div
                          key={blog.id}
                          initial={{ opacity: 0, x: -30 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true, amount: 0.2 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          whileHover={{ x: 4 }}
                        >
                          <Box
                            onClick={() => navigateToBlog(String(blog.id))}
                            sx={{
                              display: "flex",
                              gap: 3,
                              overflow: "hidden",
                              background:
                                theme.palette.mode === "dark"
                                  ? `linear-gradient(135deg, ${alpha(
                                      "#1b1b1b",
                                      0.95,
                                    )} 0%, ${alpha("#252525", 0.95)} 100%)`
                                  : `linear-gradient(135deg, ${alpha(
                                      "#ffffff",
                                      0.95,
                                    )} 0%, ${alpha("#fafafa", 0.95)} 100%)`,
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.1,
                              )}`,
                              borderLeft: `3px solid ${alpha("#f8481c", 0.3)}`,
                              boxShadow: `0 2px 12px ${alpha(
                                theme.palette.mode === "dark"
                                  ? "#000000"
                                  : "#000000",
                                0.08,
                              )}`,
                              transition:
                                "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                              cursor: "pointer",
                              "&:hover": {
                                boxShadow: `0 4px 24px ${alpha(
                                  theme.palette.mode === "dark"
                                    ? "#000000"
                                    : "#000000",
                                  0.12,
                                )}`,
                                borderLeft: `3px solid ${alpha(
                                  "#f8481c",
                                  0.8,
                                )}`,
                                "& .blog-list-image": {
                                  transform: "scale(1.05)",
                                },
                              },
                            }}
                          >
                            {/* Image */}
                            <Box
                              sx={{
                                position: "relative",
                                width: { xs: "120px", md: "200px" },
                                minWidth: { xs: "120px", md: "200px" },
                                height: { xs: "120px", md: "200px" },
                                overflow: "hidden",
                                background: alpha("#f8481c", 0.05),
                              }}
                            >
                              <Box
                                component="img"
                                src={blog.image}
                                alt={blog.title}
                                className="blog-list-image"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  if (target && FALLBACK_IMAGE)
                                    target.src = FALLBACK_IMAGE;
                                }}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  transition:
                                    "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: `linear-gradient(90deg, ${alpha(
                                    "#f8481c",
                                    0.2,
                                  )} 0%, transparent 100%)`,
                                  opacity: 0.6,
                                }}
                              />
                              {/* Topic Badge */}
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 12,
                                  left: 12,
                                  px: 1.5,
                                  py: 0.5,
                                  background: alpha("#f8481c", 0.95),
                                  backdropFilter: "blur(10px)",
                                  boxShadow: `0 2px 12px ${alpha(
                                    "#f8481c",
                                    0.4,
                                  )}`,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    color: "#ffffff",
                                    letterSpacing: "0.5px",
                                  }}
                                >
                                  {getTopicLabel(blog.topic)}
                                </Typography>
                              </Box>
                              {authenticated && (
                                <IconButton
                                  size="small"
                                  disabled={starLoadingId === String(blog.id)}
                                  onClick={(e) =>
                                    handleStarToggle(e, String(blog.id))
                                  }
                                  aria-label={
                                    starredIds.has(String(blog.id))
                                      ? "Unstar blog"
                                      : "Star blog"
                                  }
                                  sx={{
                                    position: "absolute",
                                    top: 12,
                                    right: 12,
                                    bgcolor: alpha(
                                      theme.palette.background.paper,
                                      0.9,
                                    ),
                                    color: starredIds.has(String(blog.id))
                                      ? "#f8481c"
                                      : theme.palette.text.secondary,
                                    width: 34,
                                    height: 34,
                                    "&:hover": {
                                      bgcolor: alpha("#f8481c", 0.15),
                                      color: "#f8481c",
                                    },
                                  }}
                                >
                                  {starredIds.has(String(blog.id)) ? (
                                    <StarIcon sx={{ fontSize: 18 }} />
                                  ) : (
                                    <StarBorderIcon sx={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              )}
                            </Box>

                            {/* Content */}
                            <Stack
                              spacing={1.5}
                              p={3}
                              flex={1}
                              justifyContent="center"
                            >
                              <Typography
                                component="h2"
                                sx={{
                                  fontSize: { xs: "18px", md: "22px" },
                                  fontWeight: 700,
                                  color: theme.palette.text.primary,
                                  lineHeight: 1.3,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  transition: "color 0.3s ease",
                                  "&:hover": {
                                    color: "#f8481c",
                                  },
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatDescriptionMarkdown(
                                    typeof blog.title === "string"
                                      ? blog.title
                                      : "",
                                  ),
                                }}
                              />

                              <Stack
                                direction="row"
                                spacing={2.5}
                                flexWrap="wrap"
                              >
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                >
                                  <CalendarTodayIcon
                                    sx={{
                                      fontSize: "14px",
                                      color: "#f8481c",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: theme.palette.text.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatDate(blog.publishDate)}
                                  </Typography>
                                </Stack>
                                <Stack
                                  direction="row"
                                  spacing={0.8}
                                  alignItems="center"
                                >
                                  <PersonIcon
                                    sx={{
                                      fontSize: "14px",
                                      color: "#f8481c",
                                    }}
                                  />
                                  <Typography
                                    sx={{
                                      fontSize: "13px",
                                      color: theme.palette.text.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {blog.author}
                                  </Typography>
                                </Stack>
                              </Stack>

                              <Box
                                className="blog-description-content"
                                sx={{
                                  fontSize: "14px",
                                  color: theme.palette.text.secondary,
                                  lineHeight: 1.6,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: formatDescriptionMarkdown(
                                    typeof blog.description === "string"
                                      ? blog.description
                                      : "",
                                  ),
                                }}
                              />

                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{
                                  pt: 0.5,
                                  cursor: "pointer",
                                  width: "fit-content",
                                  "&:hover": {
                                    "& .read-more-text": {
                                      color: "#f8481c",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  className="read-more-text"
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    transition: "color 0.3s ease",
                                  }}
                                >
                                  READ MORE
                                </Typography>
                                <motion.div
                                  className="read-more-arrow"
                                  animate={{ x: [0, 4, 0] }}
                                  transition={{
                                    duration: 1.5,
                                    repeat: Infinity,
                                  }}
                                >
                                  <EastIcon
                                    sx={{
                                      fontSize: "16px",
                                      color: "#f8481c",
                                    }}
                                  />
                                </motion.div>
                              </Stack>
                            </Stack>
                          </Box>
                        </motion.div>
                      ))}
                      {filteredBlogs.length === 0 && (
                        <Box
                          sx={{
                            textAlign: "center",
                            py: 10,
                            px: 3,
                            background:
                              theme.palette.mode === "dark"
                                ? alpha("#1b1b1b", 0.6)
                                : alpha("#ffffff", 0.6),
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.1,
                            )}`,
                            borderLeft: `3px solid ${alpha("#f8481c", 0.3)}`,
                          }}
                        >
                          <ArticleIcon
                            sx={{
                              fontSize: "72px",
                              color: alpha("#f8481c", 0.3),
                              mb: 2,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "20px",
                              color: theme.palette.text.secondary,
                              fontWeight: 500,
                            }}
                          >
                            No blogs found matching your criteria.
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  )}

                  {/* Pagination - Common for all views */}
                  {filteredBlogs.length > 0 && (
                    <Box
                      sx={{
                        mt: 4,
                        pt: 4,
                        borderTop: `1px solid ${alpha(
                          theme.palette.divider,
                          0.1,
                        )}`,
                      }}
                    >
                      {/* Pagination Controls - Top Section */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          flexWrap: "wrap",
                          gap: 2,
                          mb: totalPages > 1 ? 3 : 0,
                        }}
                      >
                        {/* Items per page selector */}
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.text.secondary,
                              whiteSpace: "nowrap",
                            }}
                          >
                            Items per page:
                          </Typography>
                          <Select
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            size="small"
                            sx={{
                              minWidth: "80px",
                              height: "36px",
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? alpha("#1b1b1b", 0.8)
                                  : alpha("#ffffff", 0.8),
                              border: `1px solid ${alpha(
                                theme.palette.divider,
                                0.2,
                              )}`,
                              "&:hover": {
                                border: `1px solid ${alpha("#f8481c", 0.3)}`,
                              },
                              "& .MuiOutlinedInput-notchedOutline": {
                                border: "none",
                              },
                              "&.Mui-focused": {
                                border: `1px solid ${alpha("#f8481c", 0.5)}`,
                                boxShadow: `0 0 0 3px ${alpha("#f8481c", 0.1)}`,
                              },
                              "& .MuiSelect-select": {
                                fontSize: "14px",
                                py: 1,
                                color: theme.palette.text.primary,
                              },
                            }}
                          >
                            <MenuItem value={3}>3</MenuItem>
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={15}>15</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                          </Select>
                        </Stack>

                        {/* Go to page and Random page */}
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          {/* Go to page input */}
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                color: theme.palette.text.secondary,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Go to:
                            </Typography>
                            <TextField
                              type="number"
                              value={goToPageValue}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  (parseInt(value) >= 1 &&
                                    parseInt(value) <= totalPages)
                                ) {
                                  setGoToPageValue(value);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === "Enter") {
                                  handleGoToPage();
                                }
                              }}
                              placeholder={`1-${totalPages}`}
                              size="small"
                              inputProps={{
                                min: 1,
                                max: totalPages,
                                style: { textAlign: "center", width: "60px" },
                              }}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? alpha("#1b1b1b", 0.8)
                                      : alpha("#ffffff", 0.8),
                                  border: `1px solid ${alpha(
                                    theme.palette.divider,
                                    0.2,
                                  )}`,
                                  width: "80px",
                                  "&:hover": {
                                    border: `1px solid ${alpha(
                                      "#f8481c",
                                      0.3,
                                    )}`,
                                  },
                                  "&.Mui-focused": {
                                    border: `1px solid ${alpha(
                                      "#f8481c",
                                      0.5,
                                    )}`,
                                    boxShadow: `0 0 0 3px ${alpha(
                                      "#f8481c",
                                      0.1,
                                    )}`,
                                  },
                                  "& fieldset": {
                                    border: "none",
                                  },
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "14px",
                                  py: 1,
                                  color: theme.palette.text.primary,
                                },
                              }}
                            />
                            <motion.div
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <Box
                                onClick={handleGoToPage}
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  px: 2,
                                  py: 1,
                                  cursor: "pointer",
                                  background: alpha("#f8481c", 0.1),
                                  border: `1px solid ${alpha("#f8481c", 0.3)}`,
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: alpha("#f8481c", 0.2),
                                    border: `1px solid ${alpha(
                                      "#f8481c",
                                      0.5,
                                    )}`,
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    color: "#f8481c",
                                  }}
                                >
                                  Go
                                </Typography>
                              </Box>
                            </motion.div>
                          </Stack>

                          {/* Random page button */}
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              onClick={handleRandomPage}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 2,
                                py: 1,
                                cursor: "pointer",
                                background: alpha("#f8481c", 0.1),
                                border: `1px solid ${alpha("#f8481c", 0.3)}`,
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: alpha("#f8481c", 0.2),
                                  border: `1px solid ${alpha("#f8481c", 0.5)}`,
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  fontWeight: 600,
                                  color: "#f8481c",
                                }}
                              >
                                Random
                              </Typography>
                            </Box>
                          </motion.div>
                        </Stack>
                      </Box>

                      {/* Pagination Buttons - Only show when more than 1 page */}
                      {totalPages > 1 && (
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {/* First Page Button */}
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              onClick={() => handlePageChange(1)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                cursor:
                                  currentPage === 1 ? "not-allowed" : "pointer",
                                background:
                                  currentPage === 1
                                    ? alpha(theme.palette.divider, 0.1)
                                    : theme.palette.mode === "dark"
                                      ? alpha("#1b1b1b", 0.8)
                                      : alpha("#ffffff", 0.8),
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.2,
                                )}`,
                                transition: "all 0.3s ease",
                                opacity: currentPage === 1 ? 0.5 : 1,
                                "&:hover": {
                                  background:
                                    currentPage === 1
                                      ? undefined
                                      : alpha("#f8481c", 0.1),
                                  border:
                                    currentPage === 1
                                      ? undefined
                                      : `1px solid ${alpha("#f8481c", 0.3)}`,
                                },
                              }}
                            >
                              <FirstPageIcon
                                sx={{
                                  fontSize: "20px",
                                  color:
                                    currentPage === 1
                                      ? theme.palette.text.disabled
                                      : theme.palette.text.primary,
                                }}
                              />
                            </Box>
                          </motion.div>

                          {/* Previous Button */}
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              onClick={() => handlePageChange(currentPage - 1)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                cursor:
                                  currentPage === 1 ? "not-allowed" : "pointer",
                                background:
                                  currentPage === 1
                                    ? alpha(theme.palette.divider, 0.1)
                                    : theme.palette.mode === "dark"
                                      ? alpha("#1b1b1b", 0.8)
                                      : alpha("#ffffff", 0.8),
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.2,
                                )}`,
                                transition: "all 0.3s ease",
                                opacity: currentPage === 1 ? 0.5 : 1,
                                "&:hover": {
                                  background:
                                    currentPage === 1
                                      ? undefined
                                      : alpha("#f8481c", 0.1),
                                  border:
                                    currentPage === 1
                                      ? undefined
                                      : `1px solid ${alpha("#f8481c", 0.3)}`,
                                },
                              }}
                            >
                              <ChevronLeftIcon
                                sx={{
                                  fontSize: "20px",
                                  color:
                                    currentPage === 1
                                      ? theme.palette.text.disabled
                                      : theme.palette.text.primary,
                                }}
                              />
                            </Box>
                          </motion.div>

                          {/* Page Numbers */}
                          <Stack direction="row" spacing={0.5}>
                            {Array.from(
                              { length: totalPages },
                              (_, i) => i + 1,
                            ).map((page) => {
                              // Show first page, last page, current page, and pages around current
                              const showPage =
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 &&
                                  page <= currentPage + 1);

                              if (!showPage) {
                                // Show ellipsis
                                if (
                                  page === currentPage - 2 ||
                                  page === currentPage + 2
                                ) {
                                  return (
                                    <Typography
                                      key={page}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "40px",
                                        height: "40px",
                                        color: theme.palette.text.secondary,
                                      }}
                                    >
                                      ...
                                    </Typography>
                                  );
                                }
                                return null;
                              }

                              return (
                                <motion.div
                                  key={page}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.95 }}
                                >
                                  <Box
                                    onClick={() => handlePageChange(page)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "40px",
                                      height: "40px",
                                      px: 1.5,
                                      cursor: "pointer",
                                      background:
                                        currentPage === page
                                          ? `linear-gradient(135deg, ${alpha(
                                              "#f8481c",
                                              0.2,
                                            )} 0%, ${alpha(
                                              "#f8481c",
                                              0.15,
                                            )} 100%)`
                                          : theme.palette.mode === "dark"
                                            ? alpha("#1b1b1b", 0.8)
                                            : alpha("#ffffff", 0.8),
                                      border: `1px solid ${
                                        currentPage === page
                                          ? alpha("#f8481c", 0.5)
                                          : alpha(theme.palette.divider, 0.2)
                                      }`,
                                      borderLeft:
                                        currentPage === page
                                          ? `3px solid ${alpha("#f8481c", 0.8)}`
                                          : undefined,
                                      transition: "all 0.3s ease",
                                      "&:hover": {
                                        background:
                                          currentPage === page
                                            ? undefined
                                            : alpha("#f8481c", 0.1),
                                        border:
                                          currentPage === page
                                            ? undefined
                                            : `1px solid ${alpha(
                                                "#f8481c",
                                                0.3,
                                              )}`,
                                      },
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "14px",
                                        fontWeight:
                                          currentPage === page ? 700 : 500,
                                        color:
                                          currentPage === page
                                            ? "#f8481c"
                                            : theme.palette.text.primary,
                                      }}
                                    >
                                      {page}
                                    </Typography>
                                  </Box>
                                </motion.div>
                              );
                            })}
                          </Stack>

                          {/* Next Button */}
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              onClick={() => handlePageChange(currentPage + 1)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                cursor:
                                  currentPage === totalPages
                                    ? "not-allowed"
                                    : "pointer",
                                background:
                                  currentPage === totalPages
                                    ? alpha(theme.palette.divider, 0.1)
                                    : theme.palette.mode === "dark"
                                      ? alpha("#1b1b1b", 0.8)
                                      : alpha("#ffffff", 0.8),
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.2,
                                )}`,
                                transition: "all 0.3s ease",
                                opacity: currentPage === totalPages ? 0.5 : 1,
                                "&:hover": {
                                  background:
                                    currentPage === totalPages
                                      ? undefined
                                      : alpha("#f8481c", 0.1),
                                  border:
                                    currentPage === totalPages
                                      ? undefined
                                      : `1px solid ${alpha("#f8481c", 0.3)}`,
                                },
                              }}
                            >
                              <ChevronRightIcon
                                sx={{
                                  fontSize: "20px",
                                  color:
                                    currentPage === totalPages
                                      ? theme.palette.text.disabled
                                      : theme.palette.text.primary,
                                }}
                              />
                            </Box>
                          </motion.div>

                          {/* Last Page Button */}
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Box
                              onClick={() => handlePageChange(totalPages)}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "40px",
                                height: "40px",
                                cursor:
                                  currentPage === totalPages
                                    ? "not-allowed"
                                    : "pointer",
                                background:
                                  currentPage === totalPages
                                    ? alpha(theme.palette.divider, 0.1)
                                    : theme.palette.mode === "dark"
                                      ? alpha("#1b1b1b", 0.8)
                                      : alpha("#ffffff", 0.8),
                                border: `1px solid ${alpha(
                                  theme.palette.divider,
                                  0.2,
                                )}`,
                                transition: "all 0.3s ease",
                                opacity: currentPage === totalPages ? 0.5 : 1,
                                "&:hover": {
                                  background:
                                    currentPage === totalPages
                                      ? undefined
                                      : alpha("#f8481c", 0.1),
                                  border:
                                    currentPage === totalPages
                                      ? undefined
                                      : `1px solid ${alpha("#f8481c", 0.3)}`,
                                },
                              }}
                            >
                              <LastPageIcon
                                sx={{
                                  fontSize: "20px",
                                  color:
                                    currentPage === totalPages
                                      ? theme.palette.text.disabled
                                      : theme.palette.text.primary,
                                }}
                              />
                            </Box>
                          </motion.div>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Box
                  sx={{
                    background:
                      theme.palette.mode === "dark"
                        ? `linear-gradient(135deg, ${alpha(
                            "#1b1b1b",
                            0.98,
                          )} 0%, ${alpha("#252525", 0.98)} 100%)`
                        : `linear-gradient(135deg, ${alpha(
                            "#ffffff",
                            0.98,
                          )} 0%, ${alpha("#fafafa", 0.98)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    borderTop: `3px solid ${alpha("#f8481c", 0.6)}`,
                    boxShadow: `0 2px 12px ${alpha(
                      theme.palette.mode === "dark" ? "#000000" : "#000000",
                      0.08,
                    )}`,
                    backdropFilter: "blur(20px)",
                  }}
                >
                  <Stack spacing={0}>
                    {/* Search */}
                    <Box sx={{ p: 3, pb: 3 }}>
                      <TextField
                        fullWidth
                        placeholder="Search articles..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <SearchIcon
                              sx={{
                                fontSize: "20px",
                                color: alpha("#f8481c", 0.7),
                                mr: 1.5,
                              }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? alpha("#1b1b1b", 0.6)
                                : alpha("#ffffff", 0.8),
                            border: `1px solid ${alpha(
                              theme.palette.divider,
                              0.2,
                            )}`,
                            transition: "all 0.3s ease",
                            "&:hover": {
                              border: `1px solid ${alpha("#f8481c", 0.3)}`,
                            },
                            "&.Mui-focused": {
                              border: `1px solid ${alpha("#f8481c", 0.5)}`,
                              boxShadow: `0 0 0 3px ${alpha("#f8481c", 0.1)}`,
                            },
                            "& fieldset": {
                              border: "none",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "14px",
                            py: 1.5,
                            color: theme.palette.text.primary,
                          },
                        }}
                      />
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: "1px",
                        background: `linear-gradient(90deg, transparent 0%, ${alpha(
                          theme.palette.divider,
                          0.3,
                        )} 50%, transparent 100%)`,
                      }}
                    />

                    {/* Top Recommendations */}
                    {/* <Box sx={{ p: 3 }}>
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        mb={2.5}
                      >
                        <TrendingUpIcon
                          sx={{
                            fontSize: "22px",
                            color: "#f8481c",
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: "18px",
                            fontWeight: 700,
                            color: theme.palette.text.primary,
                          }}
                        >
                          Top Recommendations
                        </Typography>
                      </Stack>
                      <Stack spacing={2}>
                        {topBlogs.map((blog, index) => (
                          <motion.div
                            key={blog.id}
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{
                              duration: 0.5,
                              delay: 0.1 + index * 0.1,
                            }}
                            whileHover={{ x: 4 }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                p: 2,
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                background: alpha("#f8481c", 0.05),
                                border: `1px solid ${alpha("#f8481c", 0.15)}`,
                                borderLeft: `3px solid ${alpha(
                                  "#f8481c",
                                  0.3
                                )}`,
                                "&:hover": {
                                  background: alpha("#f8481c", 0.1),
                                  border: `1px solid ${alpha("#f8481c", 0.3)}`,
                                  borderLeft: `3px solid ${alpha(
                                    "#f8481c",
                                    0.6
                                  )}`,
                                  transform: "translateX(4px)",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  width: "70px",
                                  height: "70px",
                                  overflow: "hidden",
                                  flexShrink: 0,
                                  background: alpha("#f8481c", 0.1),
                                  border: `1px solid ${alpha("#f8481c", 0.2)}`,
                                }}
                              >
                                <Box
                                  component="img"
                                  src={blog.image}
                                  alt={blog.title}
                                  onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    if (target && FALLBACK_IMAGE) target.src = FALLBACK_IMAGE;
                                  }}
                                  sx={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 6,
                                    left: 6,
                                    width: "22px",
                                    height: "22px",
                                    background: "#f8481c",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    boxShadow: `0 2px 8px ${alpha(
                                      "#f8481c",
                                      0.5
                                    )}`,
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      fontWeight: 700,
                                      color: "#ffffff",
                                    }}
                                  >
                                    {index + 1}
                                  </Typography>
                                </Box>
                              </Box>
                              <Stack
                                spacing={0.5}
                                flex={1}
                                justifyContent="center"
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.3,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: formatDescriptionMarkdown(typeof blog.title === "string" ? blog.title : ""),
                                  }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: "12px",
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  {formatDate(blog.publishDate)}
                                </Typography>
                              </Stack>
                            </Box>
                          </motion.div>
                        ))}
                      </Stack>
                    </Box> */}

                    {/* Divider */}
                    <Box
                      sx={{
                        height: "1px",
                        background: `linear-gradient(90deg, transparent 0%, ${alpha(
                          theme.palette.divider,
                          0.3,
                        )} 50%, transparent 100%)`,
                      }}
                    />

                    {/* Categories */}
                    <Box sx={{ p: 3 }}>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          mb: 2.5,
                        }}
                      >
                        Categories
                      </Typography>
                      <Stack spacing={1}>
                        {blogTopics.map((topic) => {
                          const count = getTopicCount(topic.key);
                          return (
                            <motion.div
                              key={topic.key}
                              whileHover={{ x: 4 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <Box
                                onClick={() =>
                                  setSelectedTopic(
                                    selectedTopic === topic.key
                                      ? null
                                      : topic.key,
                                  )
                                }
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  px: 2,
                                  py: 1.5,
                                  cursor: "pointer",
                                  background:
                                    selectedTopic === topic.key
                                      ? alpha("#f8481c", 0.15)
                                      : "transparent",
                                  border: `1px solid ${
                                    selectedTopic === topic.key
                                      ? alpha("#f8481c", 0.4)
                                      : alpha(theme.palette.divider, 0.1)
                                  }`,
                                  borderLeft: `3px solid ${
                                    selectedTopic === topic.key
                                      ? alpha("#f8481c", 0.8)
                                      : "transparent"
                                  }`,
                                  transition: "all 0.3s ease",
                                  "&:hover": {
                                    background: alpha("#f8481c", 0.1),
                                    border: `1px solid ${alpha(
                                      "#f8481c",
                                      0.3,
                                    )}`,
                                    borderLeft: `3px solid ${alpha(
                                      "#f8481c",
                                      0.6,
                                    )}`,
                                  },
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontSize: "14px",
                                    fontWeight:
                                      selectedTopic === topic.key ? 700 : 500,
                                    color:
                                      selectedTopic === topic.key
                                        ? "#f8481c"
                                        : theme.palette.text.primary,
                                  }}
                                >
                                  {topic.label}
                                </Typography>
                                <Box
                                  sx={{
                                    px: 1.5,
                                    py: 0.5,
                                    background: alpha("#f8481c", 0.15),
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#f8481c",
                                    }}
                                  >
                                    {count}
                                  </Typography>
                                </Box>
                              </Box>
                            </motion.div>
                          );
                        })}
                      </Stack>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: "1px",
                        background: `linear-gradient(90deg, transparent 0%, ${alpha(
                          theme.palette.divider,
                          0.3,
                        )} 50%, transparent 100%)`,
                      }}
                    />

                    {/* Tags */}
                    {/* <Box sx={{ p: 3 }}>
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                          mb: 2.5,
                        }}
                      >
                        Tags
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        flexWrap="wrap"
                        useFlexGap
                      >
                        {popularTags.map((tag) => (
                          <motion.div
                            key={tag}
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <Chip
                              label={tag}
                              size="small"
                              onClick={() => handleTagClick(tag)}
                              sx={{
                                fontSize: "12px",
                                height: "32px",
                                px: 1,
                                background: selectedTags.includes(tag)
                                  ? alpha("#f8481c", 0.25)
                                  : alpha("#f8481c", 0.1),
                                color: selectedTags.includes(tag)
                                  ? "#f8481c"
                                  : theme.palette.text.secondary,
                                border: `1.5px solid ${
                                  selectedTags.includes(tag)
                                    ? alpha("#f8481c", 0.5)
                                    : alpha("#f8481c", 0.25)
                                }`,
                                borderRadius: 0,
                                fontWeight: selectedTags.includes(tag)
                                  ? 700
                                  : 600,
                                cursor: "pointer",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  background: alpha("#f8481c", 0.2),
                                  border: `1.5px solid ${alpha(
                                    "#f8481c",
                                    0.4
                                  )}`,
                                },
                              }}
                            />
                          </motion.div>
                        ))}
                      </Stack>
                    </Box> */}
                  </Stack>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Blogs;
